import { useMemo } from 'react';

import { DownloadModal } from './DownloadModal';
import { resolveDownloadScopes, type DownloadDefinition } from './types';
import type { DownloadController } from './useDownloadController';
import { GenericExportProgressModal } from '@/new/components/dialogs/export-progress-dialog';

interface Props {
  definition: DownloadDefinition;
  controller: DownloadController;
}

export const DownloadFlow = ({ definition, controller }: Props) => {
  const activeAsync = controller.asyncExport;
  const modalDefinition = useMemo(() => {
    const { scopes, defaultScopeId } = resolveDownloadScopes(definition);
    return {
      ...definition,
      scopes,
      defaultScopeId,
    };
  }, [definition]);
  const activeVariant = useMemo(
    () => (activeAsync ? definition.variants.find((v) => v.id === activeAsync.variantId) : undefined),
    [activeAsync, definition.variants],
  );

  const progressOpen = !!activeAsync && activeVariant?.mode === 'async';

  return (
    <>
      <DownloadModal
        open={controller.isOpen}
        onOpenChange={(o) => (o ? controller.open() : controller.close())}
        definition={modalDefinition}
        onAsyncStarted={controller.startAsync}
      />

      {progressOpen && activeAsync && activeVariant?.mode === 'async' && (
        <GenericExportProgressModal
          isOpen
          onClose={controller.dismissProgress}
          exportConfig={{
            progressEndpoint: activeVariant.progressEndpoint(activeAsync.exportId),
            statusEndpoint: activeVariant.statusEndpoint?.(activeAsync.exportId),
            exportId: activeAsync.exportId,
          }}
          itemName={activeVariant.label}
          isCreating
          readyDescription={activeVariant.readyDescription}
          downloadButtonText={activeVariant.downloadButtonText}
          onDownload={async () => {
            await activeVariant.downloadReady(activeAsync.exportId, activeAsync.format);
            controller.dismissProgress();
          }}
        />
      )}
    </>
  );
};
